import React from 'react';
import { Typography, Button, Flex, Card, Space, theme } from 'antd';
import {
  DownloadOutlined,
  TrophyOutlined,
  TeamOutlined,
  BarChartOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useTranslation } from '../i18n/i18n';
import PageLayout from '../components/PageLayout';

const { Title, Paragraph, Text } = Typography;

const LandingContent: React.FC = () => {
  const { t, language } = useTranslation();
  const { token } = theme.useToken();

  const features = [
    {
      icon: <TrophyOutlined style={{ fontSize: 32 }} />,
      title: t('landing.features.competitive.title'),
      description: t('landing.features.competitive.description'),
    },
    {
      icon: <TeamOutlined style={{ fontSize: 32 }} />,
      title: t('landing.features.multiplayer.title'),
      description: t('landing.features.multiplayer.description'),
    },
    {
      icon: <BarChartOutlined style={{ fontSize: 32 }} />,
      title: t('landing.features.progress.title'),
      description: t('landing.features.progress.description'),
    },
    {
      icon: <ThunderboltOutlined style={{ fontSize: 32 }} />,
      title: t('landing.features.skills.title'),
      description: t('landing.features.skills.description'),
    },
  ];

  const footerContent = (
    <Flex vertical gap={token.marginXS} align="center">
      <Text type="secondary">{t('landing.footer.text')}</Text>
      <Space split={<Text type="secondary">•</Text>}>
        <Link to="/terms" style={{ color: token.colorTextSecondary }}>
          {t('landing.footer.terms')}
        </Link>
        <Link to="/privacy" style={{ color: token.colorTextSecondary }}>
          {t('landing.footer.privacy')}
        </Link>
        <Link to="/faq" style={{ color: token.colorTextSecondary }}>
          {t('landing.footer.faq')}
        </Link>
      </Space>
    </Flex>
  );

  return (
    <PageLayout showTitle footer={footerContent}>
      <Flex vertical align="center" gap={token.marginXL}>
        <Flex
          vertical
          align="center"
          gap={token.margin}
          style={{ maxWidth: 800, textAlign: 'center' }}
        >
          <Title level={1} style={{ fontSize: 48, margin: 0 }}>
            {t('landing.hero.title')}
          </Title>
          <Paragraph style={{ fontSize: 18, color: token.colorTextSecondary }}>
            {t('landing.hero.subtitle')}
          </Paragraph>
          <Space size="large">
            <Button
              type="primary"
              size="large"
              icon={<DownloadOutlined />}
              href={`https://pages.store.office.com/addinsinstallpage.aspx?assetid=WA200009624&rs=${language}`}
              target="_blank"
            >
              {t('landing.hero.getStarted')}
            </Button>
          </Space>
        </Flex>

        <Flex vertical gap={token.marginXL} style={{ maxWidth: 1200, width: '100%' }}>
          <Title level={2} style={{ textAlign: 'center' }}>
            {t('landing.features.title')}
          </Title>
          <Flex wrap="wrap" gap={token.marginLG} justify="center">
            {features.map((feature, index) => (
              <Card
                key={index}
                hoverable
                style={{
                  flex: '1 1 280px',
                  maxWidth: 300,
                  textAlign: 'center',
                }}
              >
                <Flex vertical align="center" gap={token.margin}>
                  <div style={{ color: token.colorPrimary }}>{feature.icon}</div>
                  <Title level={4} style={{ margin: 0 }}>
                    {feature.title}
                  </Title>
                  <Paragraph style={{ margin: 0, color: token.colorTextSecondary }}>
                    {feature.description}
                  </Paragraph>
                </Flex>
              </Card>
            ))}
          </Flex>
        </Flex>

        <Flex
          vertical
          align="center"
          gap={token.margin}
          style={{ maxWidth: 800, textAlign: 'center' }}
        >
          <Title level={2}>{t('landing.howItWorks.title')}</Title>
          <Flex vertical gap={token.marginMD}>
            <Card>
              <Flex gap={token.margin} align="start">
                <Text
                  strong
                  style={{
                    fontSize: 24,
                    color: token.colorPrimary,
                    width: 32,
                    flexShrink: 0,
                    textAlign: 'left',
                  }}
                >
                  1.
                </Text>
                <Flex vertical gap={token.marginXS} style={{ flex: 1 }}>
                  <Title level={4} style={{ margin: 0, textAlign: 'left' }}>
                    {t('landing.howItWorks.step1.title')}
                  </Title>
                  <Paragraph
                    style={{ margin: 0, color: token.colorTextSecondary, textAlign: 'left' }}
                  >
                    {t('landing.howItWorks.step1.description')}
                  </Paragraph>
                </Flex>
              </Flex>
            </Card>
            <Card>
              <Flex gap={token.margin} align="start">
                <Text
                  strong
                  style={{
                    fontSize: 24,
                    color: token.colorPrimary,
                    width: 32,
                    flexShrink: 0,
                    textAlign: 'left',
                  }}
                >
                  2.
                </Text>
                <Flex vertical gap={token.marginXS} style={{ flex: 1 }}>
                  <Title level={4} style={{ margin: 0, textAlign: 'left' }}>
                    {t('landing.howItWorks.step2.title')}
                  </Title>
                  <Paragraph
                    style={{ margin: 0, color: token.colorTextSecondary, textAlign: 'left' }}
                  >
                    {t('landing.howItWorks.step2.description')}
                  </Paragraph>
                </Flex>
              </Flex>
            </Card>
            <Card>
              <Flex gap={token.margin} align="start">
                <Text
                  strong
                  style={{
                    fontSize: 24,
                    color: token.colorPrimary,
                    width: 32,
                    flexShrink: 0,
                    textAlign: 'left',
                  }}
                >
                  3.
                </Text>
                <Flex vertical gap={token.marginXS} style={{ flex: 1 }}>
                  <Title level={4} style={{ margin: 0, textAlign: 'left' }}>
                    {t('landing.howItWorks.step3.title')}
                  </Title>
                  <Paragraph
                    style={{ margin: 0, color: token.colorTextSecondary, textAlign: 'left' }}
                  >
                    {t('landing.howItWorks.step3.description')}
                  </Paragraph>
                </Flex>
              </Flex>
            </Card>
          </Flex>
        </Flex>

        <Flex vertical gap={token.marginMD} style={{ maxWidth: 800, width: '100%' }}>
          <Title level={2} style={{ textAlign: 'center', margin: 0 }}>
            {t('landing.about.title')}
          </Title>
          <Text style={{ textAlign: 'center', fontSize: token.fontSizeLG }}>
            {t('landing.about.subtitle')}
          </Text>
          <Card>
            <Flex vertical gap={token.marginMD}>
              <Title level={4} style={{ margin: 0 }}>
                {t('landing.about.creator.title')}
              </Title>
              <Flex vertical gap={token.marginXS}>
                <Text strong>{t('landing.about.creator.name')}</Text>
                <Paragraph style={{ margin: 0, color: token.colorTextSecondary }}>
                  {t('landing.about.creator.description')}
                </Paragraph>
              </Flex>
            </Flex>
          </Card>
        </Flex>
      </Flex>
    </PageLayout>
  );
};

const Landing: React.FC = () => {
  return <LandingContent />;
};

export default Landing;
